/**
 * Sidebar - Professional Role-Based Navigation
 * 
 * Features:
 * - Collapsible accordion groups
 * - Role-based visibility (Officer vs Admin/Manager)
 * - Pinned critical items at top
 * - Clean, enterprise-grade UX
 */
import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { LayoutDashboard, Shield, ShieldCheck, Building2, MapPin, Calendar, Route as RouteIcon, Users, Bell, FileText, LogOut, X, ClipboardList, Clock, DollarSign, Compass, Timer, CheckSquare, Database, UserCog, ChevronDown, ChevronRight, Target, Activity, Settings, BarChart3, Briefcase, CreditCard, AlertCircle, Layers, Radio, Zap, Lock } from 'lucide-react';
import OfficerAvatar from '@/components/common/OfficerAvatar';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_securedeck/artifacts/7gltxyqf_BSMS%20Rapid%20X.png";

// Navigation group definitions
const NAVIGATION_GROUPS = {
  // PINNED - Always visible at top (no collapse)
  PINNED: {
    label: null, // No header
    icon: null,
    defaultOpen: true,
    pinned: true,
    items: [
      { to: '/executive', icon: Radio, label: 'Command Centre', adminOnly: true },
      { to: '/my-shifts', icon: Calendar, label: 'My Shifts', officerLabel: 'My Shifts' },
      { to: '/comms', icon: Radio, label: 'Comms', officerLabel: 'Comms' },
    ]
  },
  
  // COMMAND - Command & Control
  COMMAND: {
    label: 'COMMAND',
    icon: Target,
    defaultOpen: true,
    adminOnly: true,
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/live-operations', icon: Zap, label: 'Live Operations' },
    ]
  },
  
  // OPERATIONS - Day-to-day operations
  OPERATIONS: {
    label: 'OPERATIONS',
    icon: Layers,
    defaultOpen: true,
    items: [

      { to: '/master-licences', icon: ShieldCheck, label: 'Master Licences', superAdminOnly: true },
      { to: '/shifts', icon: Calendar, label: 'All Shifts', adminOnly: true },
      { to: '/admin/patrol-plans', icon: RouteIcon, label: 'Patrol Plans', adminOnly: true },
      { to: '/roster', icon: Calendar, label: 'Roster', adminOnly: true },
      { to: '/attendance', icon: Clock, label: 'Attendance', adminOnly: true },
      { to: '/my-routes', icon: Compass, label: 'My Routes' },
      { to: '/my-forms', icon: FileText, label: 'My Forms' },
      { to: '/adhoc-jobs', icon: Zap, label: 'Adhoc Jobs' },
      { to: '/reports/adhoc', icon: Zap, label: 'Adhoc Reports' },
    { to: '/incidents', icon: AlertCircle, label: 'Incidents' },
    { to: '/audit-timeline', icon: Activity, label: 'Audit Timeline' },
    ]
  },
  
  // COMPLIANCE - Officer compliance documents
  COMPLIANCE: {
    label: 'COMPLIANCE',
    icon: ShieldCheck,
    defaultOpen: false,
    items: [
      {
        to: '/officer/master-licences',
        icon: ShieldCheck,
        label: 'Master Licence',
        officerLabel: 'Master Licence'
      },
      {
        to: '/onboarding/my',
        icon: ClipboardList,
        label: 'My Onboarding'
      },
      {
        to: '/compliance/onboarding',
        icon: ClipboardList,
        label: 'Onboarding Admin',
        adminOnly: true
      },
      {
        to: '/admin/contractor-onboarding',
        icon: UserCog,
        label: 'Contractor Onboarding',
        superAdminOnly: true
      },
      {
        to: '/compliance/agreements/templates',
        icon: FileText,
        label: 'Agreements & eSign',
        adminOnly: true
      },
    ]
  },

  // WORKFORCE - People management
  WORKFORCE: {
    label: 'WORKFORCE',
    icon: Users,
    defaultOpen: false,
    adminOnly: true,
    items: [
      { to: '/users', icon: Users, label: 'Workforce', superAdminOnly: true },

      { to: '/sites', icon: MapPin, label: 'Sites' },
    ]
  },
  
  // CLIENTS - Client management
  CLIENTS: {
    label: 'CLIENTS',
    icon: Briefcase,
    defaultOpen: false,
    superAdminOnly: true,
    items: [
      { to: '/clients', icon: Building2, label: 'Clients' },
    ]
  },
  
  // FINANCE - Payroll & Timesheets
  FINANCE: {
    label: 'FINANCE',
    icon: CreditCard,
    defaultOpen: false,
    items: [
      { to: '/my-timesheets', icon: Timer, label: 'My Timesheets' },
      { to: '/timesheet-approvals', icon: CheckSquare, label: 'Approvals', adminOnly: true },
      { to: '/payroll', icon: DollarSign, label: 'Payroll', superAdminOnly: true },
        { to: '/contractor-pay', icon: DollarSign, label: 'Contractor Pay', superAdminOnly: true },
    ]
  },
  
  // REPORTING - Reports & Analytics
  REPORTING: {
    label: 'REPORTING',
    icon: BarChart3,
    defaultOpen: false,
    adminOnly: true,
    items: [
      { to: '/reports', icon: FileText, label: 'Reports' },
      { to: '/reporting-groups', icon: Layers, label: 'Reporting Groups', superAdminOnly: true },
      { to: '/forms-register', icon: ClipboardList, label: 'Forms Register', adminOnly: true },
      { to: '/forms-studio', icon: FileText, label: 'Forms Studio', adminOnly: true },
      
      { to: '/compliance-reports', icon: ShieldCheck, label: 'Compliance' },
    ]
  },
  
  // SYSTEM - System administration
  SYSTEM: {
    label: 'SYSTEM',
    icon: Settings,
    defaultOpen: false,
    superAdminOnly: true,
    items: [
      { to: '/audit', icon: ClipboardList, label: 'Audit Log' },
      { to: '/feed-management', icon: Database, label: 'Data Feeds' },
      { to: '/admin/q-review', icon: Activity, label: 'Q Review Console' },
      { to: '/admin/diagnostics', icon: Activity, label: 'Diagnostics' },
    ]
  },
};

// Navigation Group Component with Accordion
const NavGroup = ({ groupKey, group, isOpen, onToggle, userRole, onNavigate }) => {
  const { user } = useAuth();
  
  const adminRoles = ['super_admin', 'admin', 'operations_admin'];
  const supervisorRoles = [...adminRoles, 'supervisor'];
  const superAdminRoles = ['super_admin', 'admin'];
  
  const isOfficer = userRole === 'security_officer';
  const isAdmin = supervisorRoles.includes(userRole);
  const isSuperAdmin = superAdminRoles.includes(userRole);
  
  // Check group-level visibility
  if (group.adminOnly && isOfficer) return null;
  if (group.superAdminOnly && !isSuperAdmin) return null;
  
  // Filter items based on role
  const visibleItems = group.items.filter(item => {
    if (item.adminOnly && isOfficer) return false;
    if (item.superAdminOnly && !isSuperAdmin) return false;
    return true;
  });
  
  // Don't render empty groups
  if (visibleItems.length === 0) return null;
  
  // Pinned items render without header/accordion
  if (group.pinned) {
    return (
      <div className="space-y-1 mb-2">
        {visibleItems.map(item => (
          <NavItem 
            key={item.to} 
            item={item} 
            isOfficer={isOfficer}
            onNavigate={onNavigate}
          />
        ))}
      </div>
    );
  }
  
  return (
    <div className="mb-1">
      {/* Group Header - Clickable Accordion */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-zinc-500 uppercase tracking-wider hover:text-zinc-400 transition-colors"
        data-testid={`nav-group-${groupKey.toLowerCase()}`}
      >
        <div className="flex items-center gap-2">
          {group.icon && <group.icon className="w-4 h-4 shrink-0" />}
          <span>{group.label}</span>
        </div>
        {isOpen ? (
          <ChevronDown className="w-4 h-4 shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 shrink-0" />
        )}
      </button>
      
      {/* Group Items - Collapsible */}
      <div className={`space-y-0.5 overflow-hidden transition-all duration-200 ${
        isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
      }`}>
        {visibleItems.map(item => (
          <NavItem 
            key={item.to} 
            item={item}
            isOfficer={isOfficer}
            onNavigate={onNavigate}
          />
        ))}
      </div>
    </div>
  );
};

// Individual Navigation Item
const NavItem = ({ item, isOfficer, onNavigate }) => {
  const label = isOfficer && item.officerLabel ? item.officerLabel : item.label;
  
  return (
    <NavLink
      to={item.to}
      className={({ isActive }) => `
        flex items-center gap-3 px-4 py-3 min-h-[46px]
        rounded-xl text-[15px] font-medium
        tracking-[0.01em]
        transition-all duration-200 ml-2
        ${isActive 
          ? 'bg-blue-500/15 text-blue-300 border border-blue-500/30 shadow-sm shadow-blue-500/10' 
          : 'text-zinc-400 hover:text-white hover:bg-zinc-800/70'
        }
      `}
      onClick={onNavigate}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <item.icon className="w-5 h-5 shrink-0" strokeWidth={1.5} />
      <span className="truncate">{label}</span>
    </NavLink>
  );
};

// Main Sidebar Component
export function Sidebar({ isOpen, onClose, onLogout }) {
  const { user } = useAuth();
  const [openGroups, setOpenGroups] = useState({});
  
  // Initialize open groups based on defaults
  useEffect(() => {
    const initial = {};
    Object.entries(NAVIGATION_GROUPS).forEach(([key, group]) => {
      initial[key] = group.defaultOpen;
    });
    setOpenGroups(initial);
  }, []);
  
  const toggleGroup = (groupKey) => {
    setOpenGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };
  
  const handleNavigation = () => {
    // Close sidebar on mobile after navigation
    if (window.innerWidth < 1024) {
      onClose?.();
    }
  };
  
  const getRoleLabel = (role) => {
    const labels = {
      super_admin: 'Super Admin',
      admin: 'Admin',
      operations_admin: 'Ops Admin',
      supervisor: 'Supervisor',
      security_officer: 'Officer',
      contractor_admin: 'Contractor Admin',
      client_viewer: 'Client'
    };
    return labels[role] || role;
  };
  
  const getRoleBadgeColor = (role) => {
    const colors = {
      super_admin: 'border-purple-500/50 text-purple-400',
      admin: 'border-blue-500/50 text-blue-400',
      operations_admin: 'border-cyan-500/50 text-cyan-400',
      supervisor: 'border-emerald-500/50 text-emerald-400',
      security_officer: 'border-amber-500/50 text-amber-400',
      contractor_admin: 'border-orange-500/50 text-orange-400',
      client_viewer: 'border-zinc-500/50 text-zinc-400'
    };
    return colors[role] || 'border-zinc-600 text-zinc-500';
  };

  return (
    <aside className={`
      fixed inset-0 z-[9999] w-screen max-w-none h-screen lg:inset-y-0 lg:left-0 lg:z-50 lg:w-72 lg:max-w-none lg:h-screen
      bg-[#0f0f12]/95 backdrop-blur-xl border-r border-zinc-800
      flex flex-col
      transform transition-transform duration-200 ease-in-out
      overflow-hidden lg:translate-x-0 lg:fixed
      ${isOpen ? 'translate-x-0' : '-translate-x-full'}
    `}>
      {/* Logo Header */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-zinc-800 flex-shrink-0">
        <div className="flex items-center gap-3">
          <img 
            src={LOGO_URL} 
            alt="BSMS Rapid X" 
            className="h-10 w-auto object-contain"
          />
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="lg:hidden text-zinc-500 hover:text-zinc-300"
          onClick={onClose}
        >
          <X className="w-5 h-5" />
        </Button>
      </div>

      {/* Navigation - Scrollable */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 custom-scrollbar">

        {user?.role === 'contractor_admin' && (
          <div className="space-y-1 mb-2">

            <NavLink
              to="/contractor/dashboard"
              onClick={handleNavigation}
              className={({ isActive }) => `
                flex items-center gap-3 px-4 py-3 min-h-[46px]
                rounded-xl text-[15px] font-medium
                tracking-[0.01em]
                transition-all duration-200 ml-2
                ${isActive
                  ? 'bg-blue-500/15 text-blue-300 border border-blue-500/30 shadow-sm shadow-blue-500/10'
                  : 'text-zinc-400 hover:text-white hover:bg-zinc-800/70'
                }
              `}
              data-testid="nav-contractor-dashboard"
            >
              <LayoutDashboard
                className="w-5 h-5 shrink-0"
                strokeWidth={1.5}
              />
              <span className="truncate">
                Dashboard
              </span>
            </NavLink>


            <NavLink
              to="/contractor/team"
              onClick={handleNavigation}
              className={({ isActive }) => `
                flex items-center gap-3 px-4 py-3 min-h-[46px]
                rounded-xl text-[15px] font-medium
                tracking-[0.01em]
                transition-all duration-200 ml-2
                ${isActive
                  ? 'bg-blue-500/15 text-blue-300 border border-blue-500/30 shadow-sm shadow-blue-500/10'
                  : 'text-zinc-400 hover:text-white hover:bg-zinc-800/70'
                }
              `}
              data-testid="nav-contractor-team"
            >
              <Users
                className="w-5 h-5 shrink-0"
                strokeWidth={1.5}
              />
              <span className="truncate">
                Team
              </span>
            </NavLink>


            <NavLink
              to="/contractor/shifts"
              onClick={handleNavigation}
              className={({ isActive }) => `
                flex items-center gap-3 px-4 py-3 min-h-[46px]
                rounded-xl text-[15px] font-medium
                tracking-[0.01em]
                transition-all duration-200 ml-2
                ${isActive
                  ? 'bg-blue-500/15 text-blue-300 border border-blue-500/30 shadow-sm shadow-blue-500/10'
                  : 'text-zinc-400 hover:text-white hover:bg-zinc-800/70'
                }
              `}
              data-testid="nav-contractor-shifts"
            >
              <Calendar
                className="w-5 h-5 shrink-0"
                strokeWidth={1.5}
              />
              <span className="truncate">
                Allocated Shifts
              </span>
            </NavLink>


            <NavLink
              to="/contractor/timesheets"
              onClick={handleNavigation}
              className={({ isActive }) => `
                flex items-center gap-3 px-4 py-3 min-h-[46px]
                rounded-xl text-[15px] font-medium
                tracking-[0.01em]
                transition-all duration-200 ml-2
                ${isActive
                  ? 'bg-blue-500/15 text-blue-300 border border-blue-500/30 shadow-sm shadow-blue-500/10'
                  : 'text-zinc-400 hover:text-white hover:bg-zinc-800/70'
                }
              `}
              data-testid="nav-contractor-timesheets"
            >
              <Clock
                className="w-5 h-5 shrink-0"
                strokeWidth={1.5}
              />
              <span className="truncate">
                Timesheets
              </span>
            </NavLink>


            <NavLink
              to="/contractor/compliance"
              onClick={handleNavigation}
              className={({ isActive }) => `
                flex items-center gap-3 px-4 py-3 min-h-[46px]
                rounded-xl text-[15px] font-medium
                tracking-[0.01em]
                transition-all duration-200 ml-2
                ${isActive
                  ? 'bg-blue-500/15 text-blue-300 border border-blue-500/30 shadow-sm shadow-blue-500/10'
                  : 'text-zinc-400 hover:text-white hover:bg-zinc-800/70'
                }
              `}
              data-testid="nav-contractor-compliance"
            >
              <ShieldCheck
                className="w-5 h-5 shrink-0"
                strokeWidth={1.5}
              />
              <span className="truncate">
                Compliance
              </span>
            </NavLink>


            <NavLink
              to="/contractor/invoices"
              onClick={handleNavigation}
              className={({ isActive }) => `
                flex items-center gap-3 px-4 py-3 min-h-[46px]
                rounded-xl text-[15px] font-medium
                tracking-[0.01em]
                transition-all duration-200 ml-2
                ${isActive
                  ? 'bg-blue-500/15 text-blue-300 border border-blue-500/30 shadow-sm shadow-blue-500/10'
                  : 'text-zinc-400 hover:text-white hover:bg-zinc-800/70'
                }
              `}
              data-testid="nav-contractor-invoices"
            >
              <FileText
                className="w-5 h-5 shrink-0"
                strokeWidth={1.5}
              />
              <span className="truncate">
                Invoice Summary
              </span>
            </NavLink>


            <NavLink
              to="/contractor/onboarding"
              onClick={handleNavigation}
              className={({ isActive }) => `
                flex items-center gap-3 px-4 py-3 min-h-[46px]
                rounded-xl text-[15px] font-medium
                tracking-[0.01em]
                transition-all duration-200 ml-2
                ${isActive
                  ? 'bg-blue-500/15 text-blue-300 border border-blue-500/30 shadow-sm shadow-blue-500/10'
                  : 'text-zinc-400 hover:text-white hover:bg-zinc-800/70'
                }
              `}
              data-testid="nav-contractor-onboarding"
            >
              <Building2
                className="w-5 h-5 shrink-0"
                strokeWidth={1.5}
              />
              <span className="truncate">
                Onboarding
              </span>
            </NavLink>

          </div>
        )}

        {user?.role !== 'contractor_admin' && Object.entries(NAVIGATION_GROUPS).map(([groupKey, group]) => (
          <NavGroup
            key={groupKey}
            groupKey={groupKey}
            group={group}
            isOpen={openGroups[groupKey]}
            onToggle={() => toggleGroup(groupKey)}
            userRole={user?.role}
            onNavigate={handleNavigation}
          />
        ))}
        
        {/* Platform Admin Link — only for platform admins / bsms super_admin */}
        {(user?.role === 'platform_admin' || (user?.role === 'super_admin' && user?.tenant_slug === 'bsms')) && (
          <div className="mt-3 pt-3 border-t border-zinc-800/60 px-1">
            <NavLink
              to="/platform"
              onClick={handleNavigation}
              className="flex items-center gap-2 px-3 py-2 rounded-md text-xs text-amber-500/70 hover:text-amber-400 hover:bg-amber-500/5 transition-colors"
              data-testid="platform-admin-link"
            >
              <Shield className="w-4 h-4 shrink-0" />
              Platform Admin
            </NavLink>
          </div>
        )}
      </nav>

      {/* User Section - Fixed at bottom */}
      <div className="flex-shrink-0 p-5 border-t border-zinc-800 bg-[#0a0a0c]/95 backdrop-blur-xl">
        <div className="flex items-center gap-3 mb-3">
          <OfficerAvatar
            user={user}
            size="md"
            className="ring-1 ring-zinc-700"
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-zinc-200 truncate font-medium">
              {user?.first_name} {user?.last_name}
            </p>
            <Badge 
              variant="outline" 
              className={`text-[10px] px-1.5 py-0 ${getRoleBadgeColor(user?.role)}`}
            >
              {getRoleLabel(user?.role)}
            </Badge>
          </div>
        </div>
        <NavLink
          to="/change-password"
          onClick={handleNavigation}
          className="flex items-center w-full px-4 py-3 mb-2 rounded-xl text-[15px] text-zinc-400 hover:text-emerald-300 hover:bg-emerald-500/10 transition-all duration-200"
          data-testid="change-password-link"
        >
          <Lock className="w-4 h-4 mr-2" />
          Change Password
        </NavLink>

        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start min-h-[46px] rounded-xl text-[15px] text-zinc-400 hover:text-red-300 hover:bg-red-500/10 transition-all duration-200"
          onClick={onLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Sign Out
        </Button>
      </div>
    </aside>
  );
}

export default Sidebar;
